import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaExchangeAlt, FaSyncAlt } from 'react-icons/fa';
import { useTheme } from '../../context/ThemeContext';
import CopyButton from './CopyButton';
import { buttonClass, fieldClass, labelClass, mutedClass, panelClass, resultClass } from '../uiStyles';

// Official rates from the Central Bank of Uzbekistan. The endpoint is public,
// sends Access-Control-Allow-Origin: *, and needs no key.
const CBU_ENDPOINT = 'https://cbu.uz/uz/arkhiv-kursov-valyut/json/';
const BASE = 'UZS';
const PREFERRED = ['USD', 'EUR', 'RUB', 'GBP', 'KZT', 'TRY', 'CNY', 'JPY'];

const nameFor = (entry, language) => {
  if (language === 'uz') return entry.CcyNm_UZ;
  if (language === 'ru') return entry.CcyNm_RU;
  return entry.CcyNm_EN;
};

const CurrencyConverter = () => {
  const { isDarkMode } = useTheme();
  const { t, i18n } = useTranslation();
  const [state, setState] = useState({ status: 'loading', entries: [], date: '' });
  const [amount, setAmount] = useState('100');
  const [pair, setPair] = useState(['USD', BASE]);

  const load = useCallback(async () => {
    setState((current) => ({ ...current, status: 'loading' }));
    try {
      const response = await fetch(CBU_ENDPOINT);
      if (!response.ok) throw new Error(`http-${response.status}`);
      const data = await response.json();
      if (!Array.isArray(data) || data.length === 0) throw new Error('empty');
      setState({ status: 'ready', entries: data, date: data[0].Date });
    } catch {
      setState({ status: 'error', entries: [], date: '' });
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Rates are quoted per Nominal units, so normalise to "soum for one unit".
  const rates = useMemo(() => {
    const table = { [BASE]: 1 };
    state.entries.forEach((entry) => {
      const rate = Number(entry.Rate) / (Number(entry.Nominal) || 1);
      if (Number.isFinite(rate) && rate > 0) table[entry.Ccy] = rate;
    });
    return table;
  }, [state.entries]);

  const options = useMemo(() => {
    const codes = Object.keys(rates);
    const rank = (code) => {
      if (code === BASE) return -1;
      const index = PREFERRED.indexOf(code);
      return index === -1 ? PREFERRED.length : index;
    };
    return codes.sort((a, b) => rank(a) - rank(b) || a.localeCompare(b));
  }, [rates]);

  const [from, to] = pair;
  const numeric = Number(String(amount).replace(/\s/g, '').replace(',', '.'));
  const converted = Number.isFinite(numeric) && rates[from] && rates[to]
    ? (numeric * rates[from]) / rates[to]
    : NaN;

  const format = (value) => value.toLocaleString(i18n.language, {
    maximumFractionDigits: value >= 100 ? 2 : 4,
  });

  const describe = (code) => {
    if (code === BASE) return t('useful.currency.soum');
    const entry = state.entries.find((item) => item.Ccy === code);
    return entry ? nameFor(entry, i18n.language) : code;
  };

  if (state.status === 'error') {
    return (
      <div className={`${panelClass(isDarkMode)} text-center space-y-3`}>
        <p className={isDarkMode ? 'text-light/80' : 'text-gray-700'}>{t('useful.currency.error')}</p>
        <button type="button" onClick={load} className={buttonClass(isDarkMode, 'primary')}>
          {t('useful.currency.retry')}
        </button>
      </div>
    );
  }

  if (state.status === 'loading') {
    return <p className={`text-center py-8 ${mutedClass(isDarkMode)}`}>{t('useful.currency.loading')}</p>;
  }

  return (
    <div className="space-y-4">
      <div className="grid gap-3 sm:grid-cols-[1fr_auto_1fr] items-end">
        <div>
          <label className={labelClass(isDarkMode)} htmlFor="currency-amount">
            {t('useful.currency.amount')}
          </label>
          <div className="flex gap-2">
            <input
              id="currency-amount"
              value={amount}
              onChange={(event) => setAmount(event.target.value)}
              inputMode="decimal"
              className={`${fieldClass(isDarkMode)} font-mono`}
            />
            <select
              value={from}
              onChange={(event) => setPair([event.target.value, to])}
              aria-label={t('useful.currency.from')}
              className={`${fieldClass(isDarkMode)} w-28`}
            >
              {options.map((code) => <option key={code} value={code}>{code}</option>)}
            </select>
          </div>
        </div>

        <button
          type="button"
          onClick={() => setPair([to, from])}
          aria-label={t('useful.swap')}
          className={`${buttonClass(isDarkMode)} h-[42px] px-3`}
        >
          <FaExchangeAlt />
        </button>

        <div>
          <label className={labelClass(isDarkMode)} htmlFor="currency-to">
            {t('useful.currency.to')}
          </label>
          <select
            id="currency-to"
            value={to}
            onChange={(event) => setPair([from, event.target.value])}
            className={fieldClass(isDarkMode)}
          >
            {options.map((code) => <option key={code} value={code}>{code}</option>)}
          </select>
        </div>
      </div>

      <div className={panelClass(isDarkMode)}>
        <p className={`text-xs mb-1 ${mutedClass(isDarkMode)}`}>
          {describe(from)} → {describe(to)}
        </p>
        <p className={resultClass(isDarkMode)}>
          {Number.isFinite(converted) ? `${format(converted)} ${to}` : t('useful.currency.invalid')}
        </p>
        {Number.isFinite(converted) && (
          <p className={`text-sm mt-2 ${mutedClass(isDarkMode)}`}>
            1 {from} = {format(rates[from] / rates[to])} {to}
          </p>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <CopyButton value={Number.isFinite(converted) ? format(converted) : ''} />
        <button
          type="button"
          onClick={load}
          className={`${buttonClass(isDarkMode)} inline-flex items-center gap-2`}
        >
          <FaSyncAlt /> {t('useful.currency.refresh')}
        </button>
        <span className={`text-xs ${mutedClass(isDarkMode)}`}>
          {t('useful.currency.source', { date: state.date })}
        </span>
      </div>
    </div>
  );
};

export default CurrencyConverter;
